import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { isValidIBAN } from 'ibantools';
import IconCopy from '../../icons/copy-two';
import IconLoader from '../../icons/loader';
import IconArrowRight from '../../icons/arrow-right';
import IconAttention from '../../icons/attention';
import SidepanelTextRow from '../sidepanel-text-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import IconInfo from '../../icons/info';
import IconSelected from '../../icons/selected';
import {
  CASP,
  Country,
  OutgoingTransaction,
  OutgoingTransactionOrder,
  PaymentType,
} from '../types';
import { SelectItemProps } from '../../select/types';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import SidepanelSortCodeRow from '../sidepanel-sort-code-row';
import ButtonPseudo from '../../button/button-pseudo';
import formatBalance from '../../../helpers/format-balance';
import { Pagination, PermissionEnum } from '../../../base-types';
import { BicSearch, BicValidation, IbanValidation } from './types';
import ButtonSecondary from '../../button/button-secondary';
import { ButtonSize } from '../../button/types';
import ClickOutsideDetector from '../../autocomplete/click-outside-detector';

import styles from '../transaction.module.css';

interface OutgoingOrderContentProps {
  order: OutgoingTransactionOrder;
  isLoading: boolean;
  beneficiaryTypes?: SelectItemProps[];
  countries?: SelectItemProps[];
  control?: Control;
  isEditable?: boolean;
  errors?: FieldErrors<OutgoingTransaction>;
  paymentType?: PaymentType;
  onScrollToPaymentTypeField?: () => void;
  benificiaryCountryName?: string;
  benificiaryBankCountryName?: string;
  intermediaryBankCountryName?: string;
  activePermission?: PermissionEnum[];
  fields?: any;
  caspList?: CASP[];
  currencyId?: string;
  transactionId?: string;
  currencies?: SelectItemProps[];
  ibanValidationAvailable?: boolean;
  onValidateIban?: (data: string) => void;
  ibanValidationValues?: IbanValidation;
  ibanValidationIsLoading?: boolean;
  setValue?: UseFormSetValue<any>;
  bicSearchAvailable?: boolean;
  bicValidationAvailable?: boolean;
  onSearchBic?: (
    bankName: string,
    type: 'beneficiary' | 'intermediary',
  ) => void;
  onBicValidate?: (bic: string, type: 'beneficiary' | 'intermediary') => void;
  bicSearchIsLoading?: boolean;
  beneficairyBicSearchResults?: BicSearch[];
  intermediaryBicSearchResults?: BicSearch[];
  selectedValidationType?: string;
  bicValidationIntermediaryResult?: BicValidation;
  bicValidationBeneficiaryResult?: BicValidation;
  bicValidationIsLoading?: boolean;
  countriesList?: Country[];
  setChangedPaymentType: (data: string) => void;
  setChangedPaymentTypeHintVisible: (data: boolean) => void;
  setBicValidationBeneficiaryResult?: (data: BicValidation | undefined) => void;
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  setIbanValidationValues?: (data: IbanValidation | undefined) => void;
  purposeCodeVisible?: boolean;
  purposeCodes?: SelectItemProps[];
  purposeCodesIsLoading?: boolean;
  purposeCodesNextPageIsLoading?: boolean;
  onScrollPurposeCodes?: () => void;
  purposeCodesPagination?: Pagination;
  onSearchAutocomplete?: (name: string, search: string) => void;
}

let ibanTimeout: NodeJS.Timeout;
let bicTimeout: NodeJS.Timeout;

// const bicRegex = /^[A-Z0-9]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;

const getIbanErrorsText = (error: string) => {
  let errorText: string;

  switch (error) {
    case 'ACCOUNT_CHECK_DIGIT_INCORRECT':
      errorText = 'The bank account number is invalid';
      break;
    case 'IBAN_CHECK_DIGIT_INCORRECT':
      errorText = 'The IBAN is invalid';
      break;
    case 'IBAN_LENGTH_INCORRECT':
      errorText = 'The IBAN length is incorrect';
      break;
    case 'IBAN_STRUCTURE_INCORRECT':
      errorText = 'The IBAN format is incorrect';
      break;
    case 'IBAN_ILLEGAL_CHARACTERS':
      errorText = 'The IBAN contains invalid characters';
      break;
    case 'COUNTRY_DOES_NOT_SUPPORT_IBAN':
      errorText = 'The selected country does not support IBAN';
      break;
    default:
      errorText = error;
      break;
  }

  return errorText;
};

export default function OutgoingOrderContent({
  order,
  isLoading,
  beneficiaryTypes,
  countries,
  control,
  isEditable,
  errors,
  paymentType,
  onScrollToPaymentTypeField,
  benificiaryCountryName,
  benificiaryBankCountryName,
  activePermission,
  fields,
  intermediaryBankCountryName,
  caspList,
  currencyId,
  transactionId,
  currencies,
  ibanValidationAvailable,
  onValidateIban,
  ibanValidationValues,
  ibanValidationIsLoading,
  setValue,
  bicSearchAvailable,
  bicValidationAvailable,
  onSearchBic,
  onBicValidate,
  bicSearchIsLoading,
  beneficairyBicSearchResults,
  intermediaryBicSearchResults,
  selectedValidationType,
  bicValidationIntermediaryResult,
  bicValidationBeneficiaryResult,
  bicValidationIsLoading,
  countriesList,
  setChangedPaymentType,
  setChangedPaymentTypeHintVisible,
  setBicValidationBeneficiaryResult,
  setError,
  clearErrors,
  setIbanValidationValues,
  purposeCodeVisible,
  purposeCodes,
  purposeCodesIsLoading,
  purposeCodesNextPageIsLoading,
  onScrollPurposeCodes,
  purposeCodesPagination,
  onSearchAutocomplete,
}: OutgoingOrderContentProps): React.JSX.Element {
  const [accountNumber, setAccountNumber] = useState('');
  const [availableCasp, setAvailableCasp] = useState<CASP[]>([]);
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [collectorContact, setCollectorContact] = useState('');
  const [beneficiaryBanksVisible, setBeneficiaryBanksVisible] = useState(false);
  const [intermediaryBanksVisible, setIntermediaryBanksVisible] =
    useState(false);
  const [beneficiaryBicResultVisible, setBeneficiaryBicResultVisibile] =
    useState(false);
  const [intermediaryBicResultVisible, setIntermidearyBicResultVisible] =
    useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  let content;
  const enableEdit = Boolean(control);

  let fieldsWithPaymentTypeBeneficiary;

  let benificiaryCountryValue = '';
  let benificiaryBankCountryValue = '';
  let intermediaryBankCountryValue = '';

  if (!control) {
    benificiaryCountryValue = benificiaryCountryName as string;
    benificiaryBankCountryValue = benificiaryBankCountryName as string;
    intermediaryBankCountryValue = intermediaryBankCountryName as string;
  } else if (enableEdit) {
    benificiaryCountryValue = order?.benificiaryCountry as string;
    benificiaryBankCountryValue = order?.benificiaryBankCountry as string;
    intermediaryBankCountryValue = order?.intermediaryBankCountry as string;
  }

  const onCopy = () => {
    if (order?.externalId) {
      copy(order?.externalId);

      setCopyIsSuccess(true);

      setTimeout(() => {
        setCopyIsSuccess(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (order && order.recipientAccountNumber) {
      setAccountNumber(order.recipientAccountNumber);
    }
  }, [order]);

  useEffect(() => {
    if (order && order.collectorContact) {
      setCollectorContact(order.collectorContact);
    }

    if (order && order.benificiaryFullName) {
      setBeneficiaryName(order.benificiaryFullName);
    }
  }, [order]);

  useEffect(() => {
    if (
      paymentType === PaymentType.CRYPTO_ASSET_PAYMENT &&
      currencyId &&
      caspList
    ) {
      const availableCasps: CASP[] = [];

      caspList.forEach((item) => {
        if (
          item.currencies?.find(
            (currency) => currency.currencyId === currencyId,
          )
        ) {
          availableCasps.push(item);
        }
      });

      setAvailableCasp(availableCasps);
    }
  }, [paymentType, caspList, currencyId]);

  useEffect(() => {
    if (
      selectedValidationType === 'beneficiary' &&
      bicValidationBeneficiaryResult &&
      !bicValidationIsLoading
    ) {
      if (bicValidationBeneficiaryResult.bicValid) {
        setBeneficiaryBicResultVisibile(true);
      } else if (
        !bicValidationBeneficiaryResult.bicValid &&
        beneficiaryBicResultVisible
      ) {
        setBeneficiaryBicResultVisibile(false);
      }
    }

    if (
      selectedValidationType === 'intermediary' &&
      bicValidationIntermediaryResult &&
      !bicValidationIsLoading
    ) {
      if (bicValidationIntermediaryResult.bicValid) {
        setIntermidearyBicResultVisible(true);
      } else if (
        !bicValidationIntermediaryResult.bicValid &&
        !intermediaryBicResultVisible
      ) {
        setIntermidearyBicResultVisible(false);
      }
    }
  }, [
    selectedValidationType,
    bicValidationBeneficiaryResult,
    bicValidationIntermediaryResult,
    bicValidationIsLoading,
  ]);

  if (
    paymentType === PaymentType.FASTER_PAYMENT ||
    paymentType === PaymentType.BACS
  ) {
    fieldsWithPaymentTypeBeneficiary = (
      <SidepanelTextRow
        title="Account Number"
        value={enableEdit ? '' : order?.recipientAccountNumber}
        name="recipientAccountNumber"
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        placeholder="Enter Beneficiary Account number"
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable || Boolean(transactionId && order)}
        tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
        error={errors?.recipientAccountNumber?.message}
        copyEnabled={Boolean(transactionId && order)}
        copyValue={
          enableEdit
            ? fields?.recipientAccountNumber
            : order?.recipientAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  } else if (
    paymentType === PaymentType.SEPA_SCT ||
    paymentType === PaymentType.SEPA_SCT_INST
  ) {
    let notSupportTitle;
    let notSupportDescription = '';
    let paymentTypeForSwitch = '';

    if (
      (paymentType === PaymentType.SEPA_SCT ||
        paymentType === PaymentType.SEPA_SCT_INST) &&
      !ibanValidationValues?.sepaSCT &&
      !ibanValidationValues?.sepaSCI
    ) {
      notSupportTitle = 'Euro Transfer';
      notSupportDescription = 'SWIFT';

      paymentTypeForSwitch = PaymentType.SWIFT;
    } else if (
      paymentType === PaymentType.SEPA_SCT &&
      !ibanValidationValues?.sepaSCT
    ) {
      notSupportTitle = 'SEPA SCT';
      notSupportDescription = 'SWIFT';
      paymentTypeForSwitch = PaymentType.SWIFT;

      if (ibanValidationValues?.sepaSCI) {
        notSupportDescription = 'SEPA SCT Inst';
        paymentTypeForSwitch = PaymentType.SEPA_SCT_INST;
      }
    } else if (
      paymentType === PaymentType.SEPA_SCT_INST &&
      !ibanValidationValues?.sepaSCI
    ) {
      notSupportTitle = 'SEPA SCT Inst';
      notSupportDescription = 'SWIFT';
      paymentTypeForSwitch = PaymentType.SWIFT;

      if (ibanValidationValues?.sepaSCT) {
        notSupportDescription = 'SEPA SCT';
        paymentTypeForSwitch = PaymentType.SEPA_SCT;
      }
    }

    fieldsWithPaymentTypeBeneficiary = (
      <div className={styles.ibanWrapper}>
        <SidepanelTextRow
          title="IBAN"
          value={enableEdit ? '' : order?.recipientAccountNumber}
          name="recipientAccountNumber"
          placeholder="Enter Beneficiary IBAN"
          control={control}
          enableEdit={enableEdit}
          fieldValue={accountNumber}
          setFieldValue={(data: string) => {
            clearTimeout(ibanTimeout);

            setAccountNumber(data);

            if (ibanValidationAvailable && onValidateIban) {
              if (data.length) {
                if (isValidIBAN(data?.toUpperCase())) {
                  ibanTimeout = setTimeout(() => {
                    onValidateIban(data.toUpperCase());

                    if (clearErrors) {
                      clearErrors(['recipientAccountNumber']);
                    }
                  }, 700);
                } else {
                  if (setIbanValidationValues) {
                    setIbanValidationValues(undefined);
                  }

                  ibanTimeout = setTimeout(() => {
                    if (setError) {
                      setError('recipientAccountNumber', {
                        type: 'custom',
                        message: 'IBAN is Invalid',
                      });
                    }
                  }, 700);
                }
              }
            }
          }}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={
            errors?.recipientAccountNumber?.message ||
            errors?.iban?.message ||
            (ibanValidationValues &&
            ibanValidationValues?.validationErrors?.length
              ? ' '
              : '')
          }
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.recipientAccountNumber
              : order?.recipientAccountNumber
          }
          clearErrors={clearErrors}
        />
        {ibanValidationValues &&
          ibanValidationValues?.validationErrors?.length > 0 && (
            <ul className={styles.ibanErrorsList}>
              {ibanValidationValues?.validationErrors?.map((item) => (
                <li key={item}>{getIbanErrorsText(item)}</li>
              ))}
            </ul>
          )}
        {ibanValidationIsLoading && (
          <IconLoader
            className={styles.ibanValidationLoader}
            width={24}
            height={24}
          />
        )}
        {ibanValidationValues?.valid && (
          <IconSelected
            className={`${styles.ibanValidationLoader} ${styles.green}`}
            width={20}
            height={20}
          />
        )}
        {paymentType &&
          ibanValidationValues &&
          ibanValidationValues.valid &&
          (((paymentType === PaymentType.SEPA_SCT ||
            paymentType === PaymentType.SEPA_SCT_INST) &&
            !ibanValidationValues.sepaSCT &&
            !ibanValidationValues.sepaSCI) ||
            (paymentType === PaymentType.SEPA_SCT &&
              !ibanValidationValues.sepaSCT) ||
            (paymentType === PaymentType.SEPA_SCT_INST &&
              !ibanValidationValues.sepaSCI)) && (
            <div className={styles.paymentTypeNotSupportWrapper}>
              <div className={styles.paymentTypeNotSupportWrapperLeft}>
                <IconAttention width={24} height={24} />
              </div>
              <div className={styles.paymentTypeNotSupportWrapperRight}>
                <div className={styles.paymentTypeNotSupportWrapperTitle}>
                  This bank doesn&apos;t support&nbsp;{notSupportTitle}
                </div>
                <div className={styles.paymentTypeNotSupportWrapperDescription}>
                  Switch the Payment Type to {notSupportDescription} to proceed
                  with the transaction
                </div>
                <ButtonSecondary
                  size={ButtonSize.SMALL}
                  text={`Switch to ${notSupportDescription}`}
                  className="button-white"
                  onClick={() => {
                    if (setValue) {
                      setValue('paymentType', paymentTypeForSwitch);
                    }

                    setChangedPaymentType(notSupportDescription);
                    setChangedPaymentTypeHintVisible(true);
                  }}
                />
              </div>
            </div>
          )}
      </div>
    );
  } else if (paymentType === PaymentType.TARGET_2) {
    fieldsWithPaymentTypeBeneficiary = (
      <SidepanelTextRow
        title="Account Number / IBAN"
        value={enableEdit ? '' : order?.recipientAccountNumber}
        name="recipientAccountNumber"
        placeholder="Enter Beneficiary Account number or IBAN"
        control={control}
        enableEdit={enableEdit}
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        disabled={!isEditable || Boolean(transactionId && order)}
        tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
        error={errors?.recipientAccountNumber?.message}
        copyEnabled={Boolean(transactionId && order)}
        copyValue={
          enableEdit
            ? fields?.recipientAccountNumber
            : order?.recipientAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  } else if (
    paymentType === PaymentType.SWIFT ||
    paymentType === PaymentType.FEDWIRE ||
    paymentType === PaymentType.UAE_FTS ||
    paymentType === PaymentType.UAE_IPP
  ) {
    fieldsWithPaymentTypeBeneficiary = (
      <div className={styles.ibanWrapper}>
        <SidepanelTextRow
          title={
            paymentType === PaymentType.UAE_FTS ||
            paymentType === PaymentType.UAE_IPP
              ? 'IBAN'
              : `Account Number`
          }
          value={enableEdit ? '' : order?.recipientAccountNumber}
          name="recipientAccountNumber"
          fieldValue={accountNumber}
          setFieldValue={(data: string) => {
            clearTimeout(ibanTimeout);

            setAccountNumber(data);

            if (
              ibanValidationAvailable &&
              onValidateIban &&
              data.length &&
              isValidIBAN(data?.toUpperCase())
            ) {
              ibanTimeout = setTimeout(() => {
                onValidateIban(data.toUpperCase());
              }, 700);
            }
          }}
          placeholder="Enter Beneficiary Account number"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={
            errors?.recipientAccountNumber?.message ||
            errors?.iban?.message ||
            (ibanValidationValues &&
            ibanValidationValues?.validationErrors?.length
              ? ' '
              : '')
          }
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.recipientAccountNumber
              : order?.recipientAccountNumber
          }
          clearErrors={clearErrors}
        />
        {ibanValidationValues &&
          ibanValidationValues?.validationErrors?.length > 0 && (
            <ul className={styles.ibanErrorsList}>
              {ibanValidationValues?.validationErrors?.map((item) => (
                <li key={item}>{getIbanErrorsText(item)}</li>
              ))}
            </ul>
          )}
        {ibanValidationIsLoading && (
          <IconLoader
            className={styles.ibanValidationLoader}
            width={24}
            height={24}
          />
        )}
        {ibanValidationValues?.valid && (
          <IconSelected
            className={`${styles.ibanValidationLoader} ${styles.green}`}
            width={20}
            height={20}
          />
        )}
      </div>
    );
  } else if (paymentType === PaymentType.CRYPTO_ASSET_PAYMENT) {
    fieldsWithPaymentTypeBeneficiary = (
      <SidepanelTextRow
        title="Wallet Number"
        value={enableEdit ? '' : order?.recipientAccountNumber}
        name="recipientAccountNumber"
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        placeholder="Enter Beneficiary Wallet Number"
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable || Boolean(transactionId && order)}
        tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
        error={errors?.recipientAccountNumber?.message}
        copyEnabled={Boolean(transactionId && order)}
        copyValue={
          enableEdit
            ? fields?.recipientAccountNumber
            : order?.recipientAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  }

  let beneficiaryBankContent;
  let intermediaryBankContent;

  if (
    paymentType === PaymentType.SWIFT &&
    (bicSearchAvailable || bicValidationAvailable)
  ) {
    beneficiaryBankContent = (
      <>
        <SidepanelAutocompleteRow
          title="Country"
          value={benificiaryBankCountryValue}
          name="benificiaryBankCountry"
          placeholder="Choose Beneficiary Bank Country"
          control={control}
          autocompleteList={countries || []}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.benificiaryBankCountry?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? countries?.find(
                  (item) => item.value === fields?.benificiaryBankCountry,
                )?.name
              : benificiaryBankCountryValue
          }
          withClearButton
          clearErrors={clearErrors}
        />
        <div className={styles.bankNameWrapper}>
          <SidepanelTextRow
            title="Bank Name"
            value={enableEdit ? '' : order?.benificiaryBankName}
            name="benificiaryBankName"
            placeholder="Enter Beneficiary Bank Name"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(transactionId && order) ||
              !fields.benificiaryBankCountry
            }
            getFieldValue={(name) => {
              if (onSearchBic && bicSearchAvailable && name) {
                clearTimeout(bicTimeout);

                bicTimeout = setTimeout(() => {
                  onSearchBic(name, 'beneficiary');
                  setBeneficiaryBanksVisible(true);
                }, 700);
              }
            }}
            onFocus={() => {
              if (
                beneficairyBicSearchResults &&
                beneficairyBicSearchResults?.length > 0
              ) {
                setBeneficiaryBanksVisible(true);
              }
            }}
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.benificiaryBankName?.message}
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit
                ? fields?.benificiaryBankName
                : order?.benificiaryBankName
            }
            clearErrors={clearErrors}
          />
          <ClickOutsideDetector
            setOptionsListVisible={setBeneficiaryBanksVisible}
          >
            <div>
              {beneficiaryBanksVisible && (
                <div className={styles.bicBanksResult}>
                  {bicSearchIsLoading && (
                    <IconLoader
                      width={24}
                      height={24}
                      className={styles.bicBanksLoader}
                    />
                  )}
                  {!bicSearchIsLoading &&
                    beneficairyBicSearchResults &&
                    beneficairyBicSearchResults?.length > 0 &&
                    beneficairyBicSearchResults?.map((item, index) => (
                      <button
                        type="button"
                        className={styles.bicBankButton}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.bic}-${index}`}
                        onClick={() => {
                          setBeneficiaryBanksVisible(false);

                          if (setValue) {
                            setValue('benificiaryBankName', item.name);
                            setValue('benificiaryBIC', item.bic);
                            setValue(
                              'benificiaryBankAddress',
                              `${item.city}, ${item.address}`,
                            );

                            if (setBicValidationBeneficiaryResult) {
                              setBicValidationBeneficiaryResult({
                                ...item,
                                bicValid: true,
                              });
                            }
                          }
                        }}
                      >
                        <div
                          className={styles.bicBankButtonLeft}
                        >{`${item.name}, ${item.city}, ${item.address}`}</div>
                        <div className={styles.bicBankButtonRight}>
                          {item.bic}
                        </div>
                      </button>
                    ))}
                  {!bicSearchIsLoading &&
                    beneficairyBicSearchResults &&
                    !beneficairyBicSearchResults?.length && (
                      <div className={styles.emptyBanks}>No results yet</div>
                    )}
                </div>
              )}
            </div>
          </ClickOutsideDetector>
        </div>
        <div className={styles.bankNameWrapper}>
          <SidepanelTextRow
            title="BIC"
            value={enableEdit ? '' : order?.benificiaryBIC}
            name="benificiaryBIC"
            placeholder="Enter Beneficiary Bank BIC"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionId && order)}
            getFieldValue={(bic) => {
              if (onBicValidate) {
                onBicValidate(bic, 'beneficiary');
              }
            }}
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={
              errors?.benificiaryBIC?.message ||
              errors?.bic?.message ||
              (bicValidationBeneficiaryResult &&
                !bicValidationBeneficiaryResult?.bicValid)
                ? 'BIC is Invalid'
                : ''
            }
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit ? fields?.benificiaryBIC : order?.benificiaryBIC
            }
            clearErrors={clearErrors}
          />
          {bicValidationIsLoading &&
            selectedValidationType === 'beneficiary' && (
              <IconLoader
                className={styles.bicValidationLoader}
                width={24}
                height={24}
              />
            )}
          <ClickOutsideDetector
            setOptionsListVisible={setBeneficiaryBicResultVisibile}
          >
            <div>
              {beneficiaryBicResultVisible && (
                <div className={styles.bicBanksResult}>
                  {bicValidationBeneficiaryResult && (
                    <button
                      type="button"
                      className={`${styles.bicBankButton} ${
                        !bicValidationBeneficiaryResult.bicActive
                          ? styles.inactive
                          : ''
                      }`}
                      onClick={() => {
                        setBeneficiaryBicResultVisibile(false);

                        if (setValue) {
                          setValue(
                            'benificiaryBankName',
                            bicValidationBeneficiaryResult.name,
                          );
                          setValue(
                            'benificiaryBankAddress',
                            `${bicValidationBeneficiaryResult.city}, ${bicValidationBeneficiaryResult.address}`,
                          );
                          setValue(
                            'benificiaryBIC',
                            bicValidationBeneficiaryResult.bic,
                          );

                          const currentCountry = countriesList?.find(
                            (item) =>
                              item.alpha2 ===
                                bicValidationBeneficiaryResult?.isoCountryCode ||
                              item.name.toLowerCase() ===
                                bicValidationBeneficiaryResult?.countryName.toLowerCase(),
                          );

                          if (currentCountry) {
                            setValue(
                              'benificiaryBankCountry',
                              currentCountry.id,
                            );
                          }
                        }
                      }}
                    >
                      <div className={styles.bicBankButtonLeft}>
                        {bicValidationBeneficiaryResult.bic}
                      </div>
                      <div className={styles.bicBankButtonRight}>
                        {`${bicValidationBeneficiaryResult.name}, ${bicValidationBeneficiaryResult.city}, ${bicValidationBeneficiaryResult.address}`}
                        {!bicValidationBeneficiaryResult.bicActive && (
                          <div className={styles.inactiveText}>
                            BIC is Inactive
                          </div>
                        )}
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          </ClickOutsideDetector>
        </div>
      </>
    );

    intermediaryBankContent = (
      <>
        <SidepanelAutocompleteRow
          title="Country"
          value={enableEdit ? '' : order?.intermediaryBankCountry}
          name="intermediaryBankCountry"
          placeholder="Choose Intermediary Bank Country"
          control={control}
          autocompleteList={countries || []}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankCountry?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? countries?.find(
                  (item) => item.value === fields?.intermediaryBankCountry,
                )?.name
              : intermediaryBankCountryValue
          }
          clearErrors={clearErrors}
          withClearButton
        />
        <div className={styles.bankNameWrapper}>
          <SidepanelTextRow
            title="Bank Name"
            value={enableEdit ? '' : order?.intermediaryBankName}
            name="intermediaryBankName"
            placeholder="Enter Intermediary Bank Name"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(transactionId && order) ||
              !fields.intermediaryBankCountry
            }
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.intermediaryBankName?.message}
            copyEnabled={Boolean(transactionId && order)}
            getFieldValue={(name) => {
              if (onSearchBic && bicSearchAvailable && name) {
                clearTimeout(bicTimeout);

                bicTimeout = setTimeout(() => {
                  onSearchBic(name, 'intermediary');
                  setIntermediaryBanksVisible(true);
                }, 700);
              }
            }}
            onFocus={() => {
              if (
                intermediaryBicSearchResults &&
                intermediaryBicSearchResults?.length > 0
              ) {
                setIntermediaryBanksVisible(true);
              }
            }}
            copyValue={
              enableEdit
                ? fields?.intermediaryBankName
                : order?.intermediaryBankName
            }
            clearErrors={clearErrors}
          />
          <ClickOutsideDetector
            setOptionsListVisible={setIntermediaryBanksVisible}
          >
            <div>
              {intermediaryBanksVisible && (
                <div className={styles.bicBanksResult}>
                  {bicSearchIsLoading && (
                    <IconLoader
                      width={24}
                      height={24}
                      className={styles.bicBanksLoader}
                    />
                  )}
                  {!bicSearchIsLoading &&
                    intermediaryBicSearchResults &&
                    intermediaryBicSearchResults?.length > 0 &&
                    intermediaryBicSearchResults?.map((item, index) => (
                      <button
                        type="button"
                        className={styles.bicBankButton}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.bic}-${index}`}
                        onClick={() => {
                          setIntermediaryBanksVisible(false);

                          if (setValue) {
                            setValue('intermediaryBankName', item.name);
                            setValue('intermediaryBankBIC', item.bic);
                            setValue(
                              'intermediaryBankAddress',
                              `${item.city}, ${item.address}`,
                            );
                          }
                        }}
                      >
                        <div
                          className={styles.bicBankButtonLeft}
                        >{`${item.name}, ${item.city}, ${item.address}`}</div>
                        <div className={styles.bicBankButtonRight}>
                          {item.bic}
                        </div>
                      </button>
                    ))}
                  {!bicSearchIsLoading &&
                    intermediaryBicSearchResults &&
                    !intermediaryBicSearchResults?.length && (
                      <div className={styles.emptyBanks}>No results yet</div>
                    )}
                </div>
              )}
            </div>
          </ClickOutsideDetector>
        </div>
        <div className={styles.bankNameWrapper}>
          <SidepanelTextRow
            title="BIC"
            value={enableEdit ? '' : order?.intermediaryBankBIC}
            name="intermediaryBankBIC"
            placeholder="Enter Intermediary Bank BIC"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionId && order)}
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={
              errors?.intermediaryBankBIC?.message ||
              (bicValidationIntermediaryResult &&
                !bicValidationIntermediaryResult?.bicValid)
                ? 'BIC is Invalid'
                : ''
            }
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit
                ? fields?.intermediaryBankBIC
                : order?.intermediaryBankBIC
            }
            getFieldValue={(bic) => {
              if (onBicValidate) {
                onBicValidate(bic, 'intermediary');
              }
            }}
            clearErrors={clearErrors}
          />
          {bicValidationIsLoading &&
            selectedValidationType === 'intermediary' && (
              <IconLoader
                className={styles.bicValidationLoader}
                width={24}
                height={24}
              />
            )}
          <ClickOutsideDetector
            setOptionsListVisible={setIntermidearyBicResultVisible}
          >
            <div>
              {intermediaryBicResultVisible && (
                <div className={styles.bicBanksResult}>
                  {bicValidationIntermediaryResult && (
                    <button
                      type="button"
                      className={`${styles.bicBankButton} ${
                        !bicValidationIntermediaryResult.bicActive
                          ? styles.inactive
                          : ''
                      }`}
                      onClick={() => {
                        setIntermidearyBicResultVisible(false);

                        if (setValue) {
                          setValue(
                            'intermediaryBankName',
                            bicValidationIntermediaryResult.name,
                          );
                          setValue(
                            'intermediaryBankAddress',
                            `${bicValidationIntermediaryResult.city}, ${bicValidationIntermediaryResult.address}`,
                          );
                          setValue(
                            'intermediaryBankBIC',
                            bicValidationIntermediaryResult.bic,
                          );

                          const currentCountry = countriesList?.find(
                            (item) =>
                              item.alpha2 ===
                                bicValidationIntermediaryResult?.isoCountryCode ||
                              item.name.toLowerCase() ===
                                bicValidationIntermediaryResult?.countryName.toLowerCase(),
                          );

                          if (currentCountry) {
                            setValue(
                              'intermediaryBankCountry',
                              currentCountry.id,
                            );
                          }
                        }
                      }}
                    >
                      <div className={styles.bicBankButtonLeft}>
                        {bicValidationIntermediaryResult.bic}
                      </div>
                      <div className={styles.bicBankButtonRight}>
                        {`${bicValidationIntermediaryResult.name}, ${bicValidationIntermediaryResult.city}, ${bicValidationIntermediaryResult.address}`}
                        {!bicValidationIntermediaryResult.bicActive && (
                          <div className={styles.inactiveText}>
                            BIC is Inactive
                          </div>
                        )}
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          </ClickOutsideDetector>
        </div>
        <SidepanelTextRow
          title="Bank Correspondent Account"
          value={enableEdit ? '' : order?.intermediaryBankCorrespondentAccount}
          name="intermediaryBankCorrespondentAccount"
          placeholder="Enter Intermediary Bank Correspondent Account"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankCorrespondentAccount?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.intermediaryBankCorrespondentAccount
              : order?.intermediaryBankCorrespondentAccount
          }
          clearErrors={clearErrors}
        />
      </>
    );
  } else {
    beneficiaryBankContent = (
      <>
        {paymentType !== PaymentType.FASTER_PAYMENT &&
          paymentType !== PaymentType.BACS &&
          paymentType !== PaymentType.FEDWIRE && (
            <SidepanelTextRow
              title="BIC"
              value={enableEdit ? '' : order?.benificiaryBIC}
              name="benificiaryBIC"
              placeholder="Enter Beneficiary Bank BIC"
              control={control}
              enableEdit={enableEdit}
              disabled={
                !isEditable ||
                Boolean(transactionId && order) ||
                ibanValidationIsLoading
              }
              getFieldValue={(bic) => {
                if (onBicValidate) {
                  onBicValidate(bic, 'beneficiary');
                }
              }}
              tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
              error={errors?.benificiaryBIC?.message}
              copyEnabled={Boolean(transactionId && order)}
              copyValue={
                enableEdit ? fields?.benificiaryBIC : order?.benificiaryBIC
              }
              clearErrors={clearErrors}
            />
          )}
        {(paymentType === PaymentType.FASTER_PAYMENT ||
          paymentType === PaymentType.BACS) && (
          <SidepanelSortCodeRow
            title="Sort Code"
            value={order?.benificiaryBankSortCode}
            name="benificiaryBankSortCode"
            placeholder="Enter Sort Code"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(transactionId && order) ||
              ibanValidationIsLoading
            }
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.benificiaryBankSortCode?.message}
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit
                ? fields?.benificiaryBankSortCode
                : order?.benificiaryBankSortCode
            }
            clearErrors={clearErrors}
          />
        )}
        {paymentType === PaymentType.FEDWIRE && (
          <SidepanelTextRow
            title="ABA"
            value={enableEdit ? '' : order?.aba}
            name="aba"
            placeholder="Enter ABA"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(transactionId && order) ||
              ibanValidationIsLoading
            }
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.aba?.message}
            copyEnabled={Boolean(transactionId && order)}
            copyValue={enableEdit ? fields?.aba : order?.aba}
            clearErrors={clearErrors}
          />
        )}
        <SidepanelTextRow
          title="Bank Name"
          value={enableEdit ? '' : order?.benificiaryBankName}
          name="benificiaryBankName"
          placeholder="Enter Beneficiary Bank Name"
          control={control}
          enableEdit={enableEdit}
          disabled={
            !isEditable ||
            Boolean(transactionId && order) ||
            ibanValidationIsLoading
          }
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.benificiaryBankName?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.benificiaryBankName
              : order?.benificiaryBankName
          }
          clearErrors={clearErrors}
        />
        <SidepanelAutocompleteRow
          title="Country"
          value={benificiaryBankCountryValue}
          name="benificiaryBankCountry"
          placeholder="Choose Beneficiary Bank Country"
          control={control}
          autocompleteList={countries || []}
          enableEdit={enableEdit}
          disabled={
            !isEditable ||
            Boolean(transactionId && order) ||
            ibanValidationIsLoading
          }
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.benificiaryBankCountry?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? countries?.find(
                  (item) => item.value === fields?.benificiaryBankCountry,
                )?.name
              : benificiaryBankCountryValue
          }
          clearErrors={clearErrors}
          withClearButton
        />
      </>
    );

    intermediaryBankContent = (
      <>
        <SidepanelTextRow
          title="BIC"
          value={enableEdit ? '' : order?.intermediaryBankBIC}
          name="intermediaryBankBIC"
          placeholder="Enter Intermediary Bank BIC"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankBIC?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.intermediaryBankBIC
              : order?.intermediaryBankBIC
          }
          getFieldValue={(bic) => {
            if (onBicValidate) {
              onBicValidate(bic, 'intermediary');
            }
          }}
          clearErrors={clearErrors}
        />
        <SidepanelTextRow
          title="Bank Correspondent Account"
          value={enableEdit ? '' : order?.intermediaryBankCorrespondentAccount}
          name="intermediaryBankCorrespondentAccount"
          placeholder="Enter Intermediary Bank Correspondent Account"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankCorrespondentAccount?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.intermediaryBankCorrespondentAccount
              : order?.intermediaryBankCorrespondentAccount
          }
          clearErrors={clearErrors}
        />
        <SidepanelTextRow
          title="Bank Name"
          value={enableEdit ? '' : order?.intermediaryBankName}
          name="intermediaryBankName"
          placeholder="Enter Intermediary Bank Name"
          control={control}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankName?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? fields?.intermediaryBankName
              : order?.intermediaryBankName
          }
          clearErrors={clearErrors}
        />
        <SidepanelAutocompleteRow
          title="Country"
          value={enableEdit ? '' : order?.intermediaryBankCountry}
          name="intermediaryBankCountry"
          placeholder="Choose Intermediary Bank Country"
          control={control}
          autocompleteList={countries || []}
          enableEdit={enableEdit}
          disabled={!isEditable || Boolean(transactionId && order)}
          tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
          error={errors?.intermediaryBankCountry?.message}
          copyEnabled={Boolean(transactionId && order)}
          copyValue={
            enableEdit
              ? countries?.find(
                  (item) => item.value === fields?.intermediaryBankCountry,
                )?.name
              : intermediaryBankCountryValue
          }
          withClearButton
          clearErrors={clearErrors}
        />
      </>
    );
  }

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          {!paymentType && enableEdit && (
            <div className={styles.attentionWrapper}>
              <IconAttention />
              <div className={styles.attentionText}>
                First select Payment type
              </div>
              <ButtonPseudo
                text="Select"
                bold
                onClick={onScrollToPaymentTypeField}
                tabIndex={-1}
              />
            </div>
          )}
          {order?.contract && (
            <>
              <div
                className={`${styles.transactionTitle} ${styles.transactionTitleWithLink}`}
              >
                Contract
                {activePermission?.includes(
                  PermissionEnum.BENEFICIARIES_LIST,
                ) && (
                  <NavLink
                    to={`/customers/${
                      order?.beneficiary?.organization?.type === 'INDIVIDUAL'
                        ? 'individual'
                        : 'corporate'
                    }/${order?.beneficiary?.organization?.id}/beneficiaries/${
                      order?.contract?.beneficiaryId
                    }/${order?.contract?.id}`}
                    target="_blank"
                    className={styles.transactionTitleLink}
                  >
                    Contract details
                    <IconArrowRight width={12} height={12} />
                  </NavLink>
                )}
              </div>
              <SidepanelTextRow
                title="Name"
                value={order?.contract?.name}
                name="contractName"
                control={control}
                enableEdit={enableEdit}
                placeholder=""
                tabIndex={-1}
                disabled
                copyEnabled={Boolean(order)}
                copyValue={order?.contract?.name}
              />
              <SidepanelTextRow
                title="Amount"
                value={
                  order?.contract?.sum
                    ? `${formatBalance(order?.contract?.sum)} ${
                        currencies?.find(
                          (item) => item.value === order?.contract?.currencyId,
                        )?.name || ''
                      }`
                    : '-'
                }
                name="contractAmount"
                control={control}
                enableEdit={enableEdit}
                placeholder=""
                tabIndex={-1}
                disabled
                copyEnabled={Boolean(order)}
                copyValue={order?.contract?.sum ? order?.contract?.sum : '-'}
              />
              <SidepanelTextRow
                title="Description"
                value={order?.contract?.description || '-'}
                name="contractDescription"
                control={control}
                enableEdit={enableEdit}
                placeholder=""
                tabIndex={-1}
                copyEnabled={Boolean(order)}
                copyValue={order?.contract?.description || '-'}
                disabled
              />
            </>
          )}
          <div
            className={`${styles.transactionTitle} ${styles.transactionTitleWithLink}`}
          >
            Beneficiary
            {order?.beneficiary &&
              activePermission?.includes(PermissionEnum.BENEFICIARIES_LIST) && (
                <NavLink
                  to={`/customers/${
                    order?.beneficiary?.organization?.type === 'INDIVIDUAL'
                      ? 'individual'
                      : 'corporate'
                  }/${
                    order?.beneficiary?.organization?.id
                  }/beneficiaries/${order?.beneficiary?.id}`}
                  target="_blank"
                  className={styles.transactionTitleLink}
                >
                  Beneficiary details
                  <IconArrowRight width={12} height={12} />
                </NavLink>
              )}
          </div>
          {fieldsWithPaymentTypeBeneficiary}
          <SidepanelTextRow
            title="Full Name"
            value={enableEdit ? '' : order?.benificiaryFullName}
            name="benificiaryFullName"
            setFieldValue={setBeneficiaryName}
            fieldValue={beneficiaryName}
            placeholder="Enter Beneficiary Full Name"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionId && order)}
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.benificiaryFullName?.message}
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit
                ? fields?.benificiaryFullName
                : order?.benificiaryFullName
            }
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Country"
            value={benificiaryCountryValue}
            name="benificiaryCountry"
            placeholder="Choose Beneficiary Country"
            control={control}
            autocompleteList={countries || []}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionId && order)}
            tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
            error={errors?.benificiaryCountry?.message}
            copyEnabled={Boolean(transactionId && order)}
            copyValue={
              enableEdit
                ? countries?.find(
                    (item) => item.value === fields?.benificiaryCountry,
                  )?.name
                : benificiaryCountryName
            }
            clearErrors={clearErrors}
            withClearButton
          />
          {paymentType !== PaymentType.CASH_OVER_COUNTER && (
            <>
              <SidepanelSelectRow
                title="Beneficiary type"
                value={enableEdit ? '' : order?.benificiaryType}
                name="benificiaryType"
                placeholder="Choose Beneficiary Type"
                control={control}
                selectList={beneficiaryTypes || []}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionId && order)}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                error={errors?.benificiaryType?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit
                    ? beneficiaryTypes?.find(
                        (item) => item.value === fields?.benificiaryType,
                      )?.name
                    : order?.benificiaryType
                }
                clearErrors={clearErrors}
              />
              <SidepanelTextRow
                title="City"
                value={enableEdit ? '' : order?.benificiaryCity}
                name="benificiaryCity"
                placeholder="Enter Beneficiary City"
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionId && order)}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                error={errors?.benificiaryCity?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit ? fields?.benificiaryCity : order?.benificiaryCity
                }
                clearErrors={clearErrors}
              />
              <SidepanelTextRow
                title="Address"
                value={enableEdit ? '' : order?.benificiaryAddress}
                name="benificiaryAddress"
                placeholder="Enter Beneficiary Address"
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionId && order)}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                error={errors?.benificiaryAddress?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit
                    ? fields?.benificiaryAddress
                    : order?.benificiaryAddress
                }
                clearErrors={clearErrors}
              />
              {(paymentType === PaymentType.CRYPTO_ASSET_PAYMENT ||
                (order && order.blockchainTransactionHashId)) && (
                <SidepanelTextRow
                  title="Blockchain Hash Id"
                  value={enableEdit ? '' : order?.blockchainTransactionHashId}
                  name="blockchainTransactionHashId"
                  placeholder="Enter Blockchain Hash Id"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={
                    !isEditable || Boolean(transactionId && order) ? -1 : 0
                  }
                  disabled={!isEditable || Boolean(transactionId && order)}
                  error={errors?.blockchainTransactionHashId?.message}
                  copyEnabled={Boolean(transactionId && order)}
                  copyValue={
                    enableEdit
                      ? fields?.blockchainTransactionHashId
                      : order?.blockchainTransactionHashId
                  }
                  clearErrors={clearErrors}
                />
              )}
              <SidepanelTextRow
                title="Postal Code"
                value={enableEdit ? '' : order?.benificiaryAddress}
                name="benificiaryPostCode"
                placeholder="Enter Beneficiary Postal Code"
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionId && order)}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                error={errors?.benificiaryPostCode?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit
                    ? fields?.benificiaryPostCode
                    : order?.benificiaryPostCode
                }
                clearErrors={clearErrors}
              />
            </>
          )}
          {paymentType === PaymentType.CASH_OVER_COUNTER && (
            <>
              <SidepanelTextRow
                title="Collector Contact"
                value={enableEdit ? '' : order?.collectorContact}
                name="collectorContact"
                placeholder="Enter Collectors Phone number"
                control={control}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                setFieldValue={setCollectorContact}
                fieldValue={collectorContact}
                disabled={!isEditable || Boolean(transactionId && order)}
                error={errors?.collectorContact?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit
                    ? fields?.collectorContact
                    : order?.collectorContact
                }
                clearErrors={clearErrors}
              />
              <SidepanelTextRow
                title="PIN / Customer Password (optional)"
                value={enableEdit ? '' : order?.customPassword}
                name="customPassword"
                placeholder="Enter PIN or Customer Password"
                control={control}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable || Boolean(transactionId && order) ? -1 : 0
                }
                disabled={!isEditable || Boolean(transactionId && order)}
                error={errors?.customPassword?.message}
                copyEnabled={Boolean(transactionId && order)}
                copyValue={
                  enableEdit ? fields?.customPassword : order?.customPassword
                }
                clearErrors={clearErrors}
              />
            </>
          )}
        </div>
        {paymentType !== PaymentType.CRYPTO_ASSET_PAYMENT &&
          paymentType !== PaymentType.CASH_OVER_COUNTER && (
            <>
              <div className={styles.transactionBlockWrapper}>
                <div className={styles.transactionTitle}>
                  Beneficiary Bank
                  {ibanValidationIsLoading && (
                    <IconLoader
                      width={22}
                      height={22}
                      className={styles.transactionTitleLoader}
                    />
                  )}
                </div>
                {order?.externalId && (
                  <div className={styles.externalId}>
                    <div>{`External ID: ${order.externalId}`}</div>
                    <button
                      type="button"
                      onClick={onCopy}
                      className={styles.externalIdCopyButton}
                    >
                      {copyIsSuccess ? (
                        <IconSelected width={13} height={13} />
                      ) : (
                        <IconCopy width={16} height={16} />
                      )}
                    </button>
                  </div>
                )}
                {ibanValidationValues && (
                  <div className={styles.bicFilledWrapper}>
                    {ibanValidationValues?.bic &&
                      ibanValidationValues?.bank &&
                      (ibanValidationValues?.country ||
                        ibanValidationValues?.countryIso) &&
                      ibanValidationValues?.address && (
                        <>
                          <IconSelected width={14} height={14} />
                          Data filled in based on IBAN
                        </>
                      )}
                    {(!ibanValidationValues?.bic ||
                      !ibanValidationValues.bank ||
                      (!ibanValidationValues.country &&
                        !ibanValidationValues.countryIso) ||
                      !ibanValidationValues.address) && (
                      <>
                        <IconInfo
                          width={16}
                          height={16}
                          className={styles.infoIcon}
                        />
                        Some bank details found via IBAN. Please check and fill
                        in the rest
                      </>
                    )}
                    {!ibanValidationValues?.bic &&
                      !ibanValidationValues.bank &&
                      !ibanValidationValues.country &&
                      !ibanValidationValues.countryIso &&
                      !ibanValidationValues.address && (
                        <>
                          <IconInfo
                            width={16}
                            height={16}
                            className={styles.infoIcon}
                          />
                          Details not found for this IBAN. Please enter bank
                          information manually
                        </>
                      )}
                  </div>
                )}
                {beneficiaryBankContent}
                <SidepanelTextRow
                  title="Address"
                  value={enableEdit ? '' : order?.benificiaryBankAddress}
                  name="benificiaryBankAddress"
                  placeholder="Enter Beneficiary Bank Address"
                  control={control}
                  enableEdit={enableEdit}
                  disabled={
                    !isEditable ||
                    Boolean(transactionId && order) ||
                    ibanValidationIsLoading
                  }
                  tabIndex={
                    !isEditable || Boolean(transactionId && order) ? -1 : 0
                  }
                  error={errors?.benificiaryBankAddress?.message}
                  copyEnabled={Boolean(transactionId && order)}
                  copyValue={
                    enableEdit
                      ? fields?.benificiaryBankAddress
                      : order?.benificiaryBankAddress
                  }
                  clearErrors={clearErrors}
                />
                {purposeCodeVisible && paymentType && (
                  <>
                    <div className={styles.purposeCodeSeparator} />
                    <SidepanelAutocompleteRow
                      title="Purpose code"
                      value={enableEdit ? '' : order?.purposeCode}
                      name="purposeCode"
                      placeholder="Select purpose code"
                      autocompleteList={purposeCodes || []}
                      isLoading={purposeCodesIsLoading}
                      control={control}
                      enableEdit={enableEdit}
                      disabled={!isEditable || Boolean(transactionId && order)}
                      tabIndex={
                        !isEditable ||
                        !activePermission?.includes(PermissionEnum.PSP_LIST) ||
                        Boolean(transactionId && order)
                          ? -1
                          : 0
                      }
                      error={errors?.purposeCode?.message}
                      onSearchAutocomplete={onSearchAutocomplete}
                      copyValue={
                        enableEdit ? fields?.purposeCode : order?.purposeCode
                      }
                      copyEnabled={Boolean(transactionId && order)}
                      pagination={purposeCodesPagination}
                      onScroll={onScrollPurposeCodes}
                      nextPageIsLoading={purposeCodesNextPageIsLoading}
                      clearErrors={clearErrors}
                      withInfiniteScroll
                      withSuffix
                    />
                  </>
                )}
              </div>
              {(paymentType === PaymentType.SWIFT ||
                paymentType === PaymentType.TARGET_2) && (
                <div className={styles.transactionBlockWrapper}>
                  <div className={styles.transactionTitle}>
                    Intermediary Bank
                  </div>
                  {intermediaryBankContent}
                  <SidepanelTextRow
                    title="Address"
                    value={enableEdit ? '' : order?.intermediaryBankAddress}
                    name="intermediaryBankAddress"
                    placeholder="Enter Intermediary Bank Address"
                    control={control}
                    enableEdit={enableEdit}
                    disabled={!isEditable || Boolean(transactionId && order)}
                    tabIndex={
                      !isEditable || Boolean(transactionId && order) ? -1 : 0
                    }
                    error={errors?.intermediaryBankAddress?.message}
                    copyEnabled={Boolean(transactionId && order)}
                    copyValue={
                      enableEdit
                        ? fields?.intermediaryBankAddress
                        : order?.intermediaryBankAddress
                    }
                    clearErrors={clearErrors}
                  />
                </div>
              )}
            </>
          )}
        {paymentType === PaymentType.CRYPTO_ASSET_PAYMENT && (
          <div className={styles.transactionBlockWrapper}>
            <div className={styles.transactionTitle}>Benefeciary CASP</div>
            <SidepanelSelectRow
              title="CASP"
              value={enableEdit ? '' : order?.caspId}
              name="caspId"
              placeholder="Choose CASP"
              control={control}
              selectList={
                availableCasp?.map((item) => ({
                  name: item.name,
                  value: item.id,
                })) || []
              }
              enableEdit={enableEdit}
              disabled={!isEditable || Boolean(transactionId && order)}
              tabIndex={!isEditable || Boolean(transactionId && order) ? -1 : 0}
              error={errors?.caspId?.message}
              copyEnabled={Boolean(transactionId && order)}
              copyValue={
                enableEdit
                  ? availableCasp?.find((item) => item.id === fields?.caspId)
                      ?.name
                  : availableCasp?.find((item) => item.id === order?.caspId)
                      ?.name
              }
              clearErrors={clearErrors}
              withClearButton
            />
          </div>
        )}
      </>
    );
  }

  return content;
}
